import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const AI_SERVER = 'https://campus-life-ai.onrender.com';
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

function renderMarkdown(text) {
  if (!text) return '';
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

const circledToIdx = { '①': 0, '②': 1, '③': 2, '④': 3, '⑤': 4 };

function shuffleOptions(options, answerIdx) {
  const tagged = options.map((opt, i) => ({ text: opt, isAnswer: i === answerIdx }));
  for (let i = tagged.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tagged[i], tagged[j]] = [tagged[j], tagged[i]];
  }
  const newOptions   = tagged.map(t => t.text);
  const newAnswerIdx = answerIdx !== null ? tagged.findIndex(t => t.isAnswer) : null;
  return { options: newOptions, answer: newAnswerIdx };
}

const Ico = {
  Quiz:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Trophy: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 21 12 17 16 21"/><line x1="12" y1="17" x2="12" y2="11"/><path d="M7 4H4a2 2 0 0 0-2 2v1a5 5 0 0 0 5 5h10a5 5 0 0 0 5-5V6a2 2 0 0 0-2-2h-3"/><rect x="7" y="2" width="10" height="4" rx="1"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

function QuizPage() {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [quizzes,        setQuizzes]        = useState([]);
  const [selected,       setSelected]       = useState({});
  const [showResult,     setShowResult]     = useState(false);
  const [score,          setScore]          = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [grading,        setGrading]        = useState(false);
  const [gradingResults, setGradingResults] = useState({});
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMsg,       setAlertMsg]       = useState('');
  const [alertCallback,  setAlertCallback]  = useState(() => () => {});

  useEffect(() => {
    const summary = localStorage.getItem('summary');
    if (!summary) {
      setLoading(false);
      setAlertMsg('분석 결과가 없습니다. 파일을 먼저 업로드해주세요.');
      setAlertCallback(() => () => navigate('/upload'));
      setShowAlertModal(true);
      return;
    }
    setQuizzes(parseQuizFromSummary(summary));
    setLoading(false);
  }, [navigate]);

  const parseQuizFromSummary = (summary) => {
    const questions = [];
    const lines     = summary.split('\n').map(l => l.trim()).filter(Boolean);
    const examIdx   = lines.findIndex(l =>
      l.includes('예상 시험 문제') || l.includes('예상문제') || l.includes('시험 문제')
    );
    const questionLines  = examIdx >= 0 ? lines.slice(examIdx + 1) : lines;
    const questionLineRe = /^(?:문제\s*|Q\s*)?(\d+)[.)]\s*(.+)/i;
    const optionLineRe   = /^[①②③④⑤]|^[a-eA-E][).]\s/;
    const answerLineRe   = /^정답\s*[:：]\s*([①②③④⑤\d])/;

    let i = 0;
    while (i < questionLines.length) {
      const qMatch = questionLines[i].match(questionLineRe);
      if (qMatch) {
        const questionText = qMatch[2];
        const options      = [];
        let answerIdx      = null;
        i++;
        while (i < questionLines.length && optionLineRe.test(questionLines[i])) {
          const optText = questionLines[i].replace(/^[①②③④⑤]\s*/, '').replace(/^[a-eA-E][).]\s*/, '').trim();
          if (optText) options.push(optText);
          i++;
        }
        if (i < questionLines.length && answerLineRe.test(questionLines[i])) {
          const ansChar = questionLines[i].match(answerLineRe)[1];
          answerIdx = circledToIdx[ansChar] !== undefined ? circledToIdx[ansChar] : parseInt(ansChar) - 1;
          i++;
        }
        if (options.length > 0) {
          const shuffled = shuffleOptions(options, answerIdx);
          questions.push({ id: questions.length, q: questionText, options: shuffled.options, answer: shuffled.answer });
        } else {
          questions.push({ id: questions.length, q: questionText, options: [], answer: null });
        }
      } else { i++; }
    }

    if (questions.length === 0) {
      lines.forEach(line => {
        if (line.endsWith('?') || line.endsWith('하시오.') || line.endsWith('설명하시오.')) {
          questions.push({ id: questions.length, q: line, options: [], answer: null });
        }
      });
    }
    return questions;
  };

  const gradeSubjective = async (quiz, userAnswer) => {
    try {
      const res = await fetch(`${AI_SERVER}/api/ai/grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: quiz.q, answer: userAnswer }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch {
      return { correct: false, feedback: '채점 중 오류가 발생했어요. 서버 상태를 확인해주세요.' };
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(selected).length < quizzes.length) {
      setAlertMsg('모든 문제의 답을 선택(또는 입력)해주세요!');
      setAlertCallback(() => () => {});
      setShowAlertModal(true);
      return;
    }
    setGrading(true);
    const results = {};
    let correct   = 0;
    await Promise.all(quizzes.map(async (quiz) => {
      if (quiz.options.length > 0 && quiz.answer !== null) {
        const isCorrect = selected[quiz.id] === quiz.answer;
        results[quiz.id] = {
          correct: isCorrect,
          feedback: isCorrect ? '정답이에요! 🎉' : `정답은 ${['①','②','③','④','⑤'][quiz.answer]} ${quiz.options[quiz.answer]} 이에요.`,
        };
        if (isCorrect) correct++;
      } else {
        const result = await gradeSubjective(quiz, selected[quiz.id] || '');
        results[quiz.id] = result;
        if (result.correct) correct++;
      }
    }));
    setGradingResults(results);
    setScore(correct);
    setGrading(false);
    setShowResult(true);
  };

  const handleRetry = () => {
    const summary = localStorage.getItem('summary') || '';
    setQuizzes(parseQuizFromSummary(summary));
    setSelected({});
    setShowResult(false);
    setScore(0);
    setGradingResults({});
  };

  if (loading) {
    return (
      <div style={{ ...S.root, overflowX: 'hidden' }}>
        <div style={S.loadingWrap}>
          <div style={S.spinner} />
          <p style={S.loadingText}>문제를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (quizzes.length === 0) {
    const summary = localStorage.getItem('summary') || '';
    return (
      <div style={{ ...S.root, padding: isMobile ? '24px 14px 60px' : '60px 20px', overflowX: 'hidden' }}>
        <div style={S.pageTitle}>
          <h2 style={{ ...S.pageTitleText, fontSize: isMobile ? '18px' : '24px' }}>📝 AI 예상 문제</h2>
        </div>
        <div style={{ ...S.card, maxWidth: isMobile ? '100%' : '700px', padding: isMobile ? '16px 14px' : '22px 24px' }}>
          <CardHeader icon={Ico.Quiz} title="요약 내용" />
          <div style={S.summaryFallback}>
            {summary.split('\n').map((line, i) =>
              line.trim() ? <p key={i} style={S.summaryLine}>{renderMarkdown(line)}</p> : <br key={i} />
            )}
          </div>
          <div style={{ ...S.btnGroup, marginTop: '20px', flexDirection: isMobile ? 'column' : 'row' }}>
            <button style={S.secondaryBtn} onClick={() => navigate('/upload')}>다시 업로드</button>
            <button style={S.primaryBtn}   onClick={() => navigate('/main')}>홈으로 이동</button>
          </div>
        </div>
        {alertModal(showAlertModal, alertMsg, alertCallback, setShowAlertModal, isMobile)}
      </div>
    );
  }

  return (
    <div style={{ ...S.root, padding: isMobile ? '24px 14px 60px' : '60px 20px', overflowX: 'hidden' }}>

      {/* ── 채점 중 오버레이 ── */}
      {grading && (
        <div style={S.gradingOverlay}>
          <div style={S.gradingBox}>
            <div style={S.spinner} />
            <p style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '12px 0 4px' }}>🤖 AI가 채점 중이에요...</p>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>서술형 답변을 분석하고 있어요. 잠시만 기다려주세요!</p>
          </div>
        </div>
      )}

      {alertModal(showAlertModal, alertMsg, alertCallback, setShowAlertModal, isMobile)}

      {/* ── 문제 풀기 ── */}
      {!showResult ? (
        <>
          <div style={S.pageTitle}>
            <h2 style={{ ...S.pageTitleText, fontSize: isMobile ? '18px' : '24px' }}>📝 AI 예상 문제</h2>
            <p style={{ ...S.pageTitleSub, fontSize: isMobile ? '12px' : '13px' }}>AI가 생성한 예상 문제입니다. 답을 선택하고 제출하세요!</p>
          </div>

          <div style={{ ...S.card, maxWidth: isMobile ? '100%' : '700px', padding: isMobile ? '16px 14px' : '22px 24px' }}>
            <CardHeader icon={Ico.Quiz} title="문제 풀기" />

            {quizzes.map((quiz, index) => (
              <div key={quiz.id} style={{ ...S.questionBox, padding: isMobile ? '12px 14px' : '16px 18px' }}>
                <p style={S.questionNum}>문제 {index + 1} · {quiz.options.length > 0 ? '객관식' : '서술형'}</p>
                <p style={{ ...S.questionText, fontSize: isMobile ? '13px' : '14px' }}>{renderMarkdown(quiz.q)}</p>

                {quiz.options.length > 0 ? (
                  <div style={{
                    ...S.optionsGrid,
                    // 모바일: 1열
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: isMobile ? '6px' : '8px',
                  }}>
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        style={{
                          ...S.optionBtn,
                          fontSize: isMobile ? '12px' : '13px',
                          padding: isMobile ? '9px 11px' : '11px 13px',
                          backgroundColor: selected[quiz.id] === i ? BLUE   : '#f8fafc',
                          color:           selected[quiz.id] === i ? '#fff' : '#374151',
                          borderColor:     selected[quiz.id] === i ? BLUE   : '#e2e8f0',
                        }}
                        onClick={() => setSelected({ ...selected, [quiz.id]: i })}
                      >
                        {['①','②','③','④','⑤'][i]} {renderMarkdown(opt)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <textarea
                      style={{ ...S.textarea, fontSize: isMobile ? '12px' : '13px' }}
                      placeholder="답을 입력하세요... (최대 200자)"
                      value={selected[quiz.id] || ''}
                      onChange={(e) => { if (e.target.value.length <= 200) setSelected({ ...selected, [quiz.id]: e.target.value }); }}
                      maxLength={200}
                    />
                    <span style={{ position: 'absolute', right: '10px', bottom: '10px', fontSize: '11px', color: (selected[quiz.id] || '').length >= 200 ? '#ef4444' : '#94a3b8' }}>
                      {(selected[quiz.id] || '').length}/200
                    </span>
                  </div>
                )}
              </div>
            ))}

            <button
              style={{ ...S.submitBtn, fontSize: isMobile ? '14px' : '15px', padding: isMobile ? '12px' : '14px' }}
              onClick={handleSubmit}
              disabled={grading}
            >
              결과 제출하기
            </button>
          </div>
        </>

      ) : (
        /* ── 결과 화면 ── */
        <>
          <div style={S.pageTitle}>
            <h2 style={{ ...S.pageTitleText, fontSize: isMobile ? '18px' : '24px' }}>🏆 학습 결과</h2>
            <p style={{ ...S.pageTitleSub, fontSize: isMobile ? '12px' : '13px' }}>채점이 완료되었어요. 틀린 문제를 다시 확인해보세요!</p>
          </div>

          <div style={{ ...S.card, maxWidth: isMobile ? '100%' : '700px', padding: isMobile ? '16px 14px' : '22px 24px' }}>
            <CardHeader icon={Ico.Trophy} title="최종 점수" />

            <div style={{ ...S.scoreBox, padding: isMobile ? '18px 14px' : '24px' }}>
              <p style={S.scoreLabel}>학습 완료! 🎉</p>
              <p style={{ ...S.scoreNum, fontSize: isMobile ? '34px' : '42px' }}>{score} / {quizzes.length}</p>
              <p style={{ ...S.scoreMsg, fontSize: isMobile ? '13px' : '14px' }}>
                {score === quizzes.length ? '모든 문제 정답! 😎' : score >= Math.ceil(quizzes.length / 2) ? '잘 했어요! 조금만 더 복습해보세요 😊' : '복습이 필요해요! 다시 도전해보세요 🤔'}
              </p>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {quizzes.map((quiz, index) => {
                const result    = gradingResults[quiz.id];
                const isCorrect = result?.correct ?? false;
                return (
                  <div key={quiz.id} style={{ ...S.reviewItem, padding: isMobile ? '12px 13px' : '14px 16px', borderLeft: `3px solid ${isCorrect ? '#22c55e' : '#ef4444'}` }}>
                    <p style={{ ...S.reviewQ, fontSize: isMobile ? '12px' : '13px' }}>
                      {isCorrect ? '✅' : '❌'} {index + 1}. {renderMarkdown(quiz.q)}
                    </p>
                    {quiz.options.length > 0 && quiz.answer !== null && (
                      <div style={{ ...S.reviewAns, fontSize: isMobile ? '11px' : '12px' }}>
                        <span style={{ color: '#64748b' }}>내 답: </span>
                        <span style={{ color: isCorrect ? '#22c55e' : '#ef4444', fontWeight: '600' }}>
                          {selected[quiz.id] !== undefined ? `${['①','②','③','④','⑤'][selected[quiz.id]]} ${quiz.options[selected[quiz.id]]}` : '미선택'}
                        </span>
                        {!isCorrect && (
                          <span>
                            <span style={{ color: '#94a3b8' }}> · 정답: </span>
                            <span style={{ color: '#22c55e', fontWeight: '600' }}>
                              {['①','②','③','④','⑤'][quiz.answer]} {quiz.options[quiz.answer]}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {quiz.options.length === 0 && (
                      <div>
                        <div style={{ ...S.reviewAns, fontSize: isMobile ? '11px' : '12px' }}>
                          <span style={{ color: '#64748b' }}>내 답: </span>
                          <span style={{ color: '#374151' }}>{selected[quiz.id] || '(미입력)'}</span>
                        </div>
                        {result?.feedback && (
                          <div style={{ marginTop: '8px', padding: '9px 12px', backgroundColor: isCorrect ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', fontSize: isMobile ? '11px' : '12px', color: isCorrect ? '#166534' : '#991b1b', fontWeight: '500' }}>
                            🤖 {result.feedback}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ ...S.btnGroup, marginTop: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
              <button style={{ ...S.secondaryBtn, fontSize: isMobile ? '13px' : '14px', padding: isMobile ? '11px' : '13px' }} onClick={handleRetry}>🔄 다시 풀기</button>
              <button style={{ ...S.primaryBtn,   fontSize: isMobile ? '13px' : '14px', padding: isMobile ? '11px' : '13px' }} onClick={() => navigate('/main')}>홈으로 이동</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function alertModal(show, msg, callback, setShow, isMobile) {
  if (!show) return null;
  return (
    <div style={S.modalOverlay}>
      <div style={{ ...S.modal, padding: isMobile ? '24px 20px' : '32px 40px' }}>
        <p style={S.modalMsg}>{msg}</p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button style={S.modalBtn} onClick={() => { setShow(false); callback(); }}>확인</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  root: {
    minHeight: '100vh', backgroundColor: '#f4f7f9',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '60px 20px', gap: '20px', boxSizing: 'border-box',
  },
  pageTitle:     { textAlign: 'center', marginBottom: '4px' },
  pageTitleText: { fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' },
  pageTitleSub:  { fontSize: '13px', color: '#64748b', margin: 0 },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '22px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', width: '100%', maxWidth: '700px', boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  cardIcon:   { width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: BLUE_BG, color: BLUE },
  cardTitle:  { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  questionBox:  { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 18px', marginBottom: '14px' },
  questionNum:  { fontSize: '10px', fontWeight: '600', color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase', margin: '0 0 6px' },
  questionText: { fontSize: '14px', fontWeight: '600', color: '#1e293b', lineHeight: '1.6', margin: '0 0 14px' },
  optionsGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  optionBtn:    { padding: '11px 13px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', textAlign: 'left', lineHeight: '1.5', border: '1px solid #e2e8f0', transition: 'all 0.15s' },
  textarea:     { width: '100%', padding: '11px 13px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', minHeight: '80px', resize: 'vertical', backgroundColor: '#f8fafc', color: '#374151', outline: 'none', fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif", boxSizing: 'border-box' },
  submitBtn:    { width: '100%', padding: '14px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '4px' },
  scoreBox:     { backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '24px', textAlign: 'center', marginBottom: '4px' },
  scoreLabel:   { fontSize: '13px', color: '#64748b', margin: '0 0 4px' },
  scoreNum:     { fontSize: '42px', fontWeight: '800', color: BLUE, margin: '4px 0' },
  scoreMsg:     { fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: 0 },
  reviewItem:   { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px' },
  reviewQ:      { fontSize: '13px', fontWeight: '600', color: '#1e293b', margin: '0 0 6px', lineHeight: '1.5' },
  reviewAns:    { fontSize: '12px', color: '#64748b' },
  summaryFallback: { textAlign: 'left', lineHeight: '1.8', fontSize: '13px', color: '#374151' },
  summaryLine:     { margin: '3px 0' },
  btnGroup:     { display: 'flex', gap: '12px' },
  primaryBtn:   { flex: 1, padding: '13px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn: { flex: 1, padding: '13px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  gradingOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 },
  gradingBox:     { textAlign: 'center', padding: '30px' },
  loadingWrap:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 20px', gap: '14px' },
  spinner:        { width: '28px', height: '28px', border: '3px solid #bfdbfe', borderTop: `3px solid ${BLUE}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText:    { fontSize: '14px', color: '#64748b', margin: 0 },
  modalOverlay:   { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modal:          { backgroundColor: '#fff', padding: '32px 40px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', textAlign: 'center', minWidth: '280px', width: '90%', maxWidth: '360px', boxSizing: 'border-box' },
  modalMsg:       { fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: '0 0 24px', lineHeight: '1.5' },
  modalBtn:       { padding: '9px 24px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

if (typeof document !== 'undefined' && !document.getElementById('spin-style-quiz')) {
  const style       = document.createElement('style');
  style.id          = 'spin-style-quiz';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default QuizPage;